import React, { useEffect, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenancy } from '@/contexts/TenancyContext';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

// Axis labels for the heatmap
const LIKELIHOOD = ["Rare", "Unlikely", "Possible", "Likely", "Almost Certain"];
const CONSEQUENCE = ["Insignificant", "Minor", "Moderate", "Major", "Catastrophic"];

// Default empty heatmap data
const EMPTY_HEATMAP = Array(5).fill(0).map(() => Array(5).fill(0));

// Interface for the risk data from the backend
interface TreatRiskHeatmapData {
  heatmap: Record<string, number>;
  risks: Array<{
    id: string;
    riskID: number;
    componentId: string;
    displayName: string;
    residualRisk: string;
    inherentLike: number;
    inherentCons: number;
    cellClass: string;
    riskData: any;
  }>;
  totalRisks: number;
  lastUpdated: string;
}

const CELL_COLORS = {
  light: [
    "bg-green-200", // Low
    "bg-yellow-200", // Moderate
    "bg-orange-200", // High
    "bg-red-400", // Very High
    "bg-red-700 text-white", // Extreme
  ],
  dark: [
    "bg-green-900/30 border border-green-600/30", // Low
    "bg-yellow-900/30 border border-yellow-600/30", // Moderate
    "bg-orange-900/30 border border-orange-600/30", // High
    "bg-red-900/40 border border-red-600/40", // Very High
    "bg-red-900/70 border border-red-600/70 text-red-100", // Extreme
  ]
};

const EMPTY_CELL_CLASS = {
  light: "bg-gray-100",
  dark: "bg-gray-800/30 border border-gray-700/30"
};

function getCellColor(count: number, x: number, y: number, isDark: boolean) {
  const theme = isDark ? 'dark' : 'light';
  const colors = CELL_COLORS[theme];
  
  if (count === 0) return EMPTY_CELL_CLASS[theme];
  if (x + y <= 1) return colors[0];
  if (x + y === 2) return colors[1];
  if (x + y === 3) return colors[2];
  if (x + y === 4) return colors[3];
  return colors[4];
}

export const TreatRisksHeatmap = React.forwardRef<{ refreshHeatmap: () => void }, {}>((props, ref) => {
  const cellCount = 5;
  const cellGap = 4; // px
  const { isDark } = useTheme();
  const { activeTenancy } = useTenancy();
  
  const [data, setData] = useState<TreatRiskHeatmapData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHeatmapData = async () => {
    if (!activeTenancy?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await deiterateAPI.GET<TreatRiskHeatmapData>(
        '/v1/risks/heatmap/treat',
        {
          backend: 'thalassa',
          companyID: activeTenancy.id,
          headers: {
            'x-tenancy-id': activeTenancy.id,
          },
        }
      );
      
      setData(result);
    } catch (err) {
      console.error('Error fetching treat risks heatmap:', err);
      setError(err instanceof Error ? err.message : 'Failed to load heatmap data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHeatmapData();
  }, [activeTenancy?.id]);
  
  // Expose a refresh function
  const refreshHeatmap = React.useCallback(() => {
    fetchHeatmapData();
  }, [activeTenancy?.id]);
  
  // Expose the refresh function via ref
  React.useImperativeHandle(ref, () => ({
    refreshHeatmap
  }), [refreshHeatmap]);
  
  // Transform the heatmap data from the backend to the 5x5 grid format
  const heatmapData = React.useMemo(() => {
    if (!data?.heatmap) return EMPTY_HEATMAP;
    
    // Initialize a 5x5 grid with zeros
    const grid: number[][] = Array(5).fill(0).map(() => Array(5).fill(0));
    
    // Map the cell classes to grid positions
    const cellClassToPosition: Record<string, {x: number, y: number}> = {
      'OneXOne': {x: 0, y: 0}, 'OneXTwo': {x: 1, y: 0}, 'OneXThree': {x: 2, y: 0}, 'OneXFour': {x: 3, y: 0}, 'OneXFive': {x: 4, y: 0},
      'TwoXOne': {x: 0, y: 1}, 'TwoXTwo': {x: 1, y: 1}, 'TwoXThree': {x: 2, y: 1}, 'TwoXFour': {x: 3, y: 1}, 'TwoXFive': {x: 4, y: 1},
      'ThreeXOne': {x: 0, y: 2}, 'ThreeXTwo': {x: 1, y: 2}, 'ThreeXThree': {x: 2, y: 2}, 'ThreeXFour': {x: 3, y: 2}, 'ThreeXFive': {x: 4, y: 2},
      'FourXOne': {x: 0, y: 3}, 'FourXTwo': {x: 1, y: 3}, 'FourXThree': {x: 2, y: 3}, 'FourXFour': {x: 3, y: 3}, 'FourXFive': {x: 4, y: 3},
      'FiveXOne': {x: 0, y: 4}, 'FiveXTwo': {x: 1, y: 4}, 'FiveXThree': {x: 2, y: 4}, 'FiveXFour': {x: 3, y: 4}, 'FiveXFive': {x: 4, y: 4},
    };

    // Populate the grid with the counts from the backend
    for (const [cellClass, count] of Object.entries(data.heatmap)) {
      const position = cellClassToPosition[cellClass];
      if (position && typeof count === 'number') {
        grid[position.y][position.x] = count;
      }
    }

    return grid;
  }, [data]);

  return (
    <div className="mt-6 rounded-lg bg-card border p-4">
      <h4 className="text-sm font-medium text-muted-foreground mb-4">Risks Requiring Treatment</h4>
      
      <div className="flex gap-3 items-start">
        {/* Y-axis (likelihood) labels */}
        <div className="flex flex-col justify-between" style={{ width: '100px', height: '320px' }}>
          {[...LIKELIHOOD].reverse().map((l) => (
            <div key={l} className="flex items-center justify-end text-xs text-muted-foreground" style={{ height: '64px' }}>
              {l}
            </div>
          ))}
        </div>
        
        {/* Grid and X-axis container */}
        <div className="flex-1" style={{ maxWidth: '600px' }}>
          {/* Grid */}
          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns: `repeat(${cellCount}, 1fr)`,
              gridTemplateRows: `repeat(${cellCount}, 1fr)`,
              height: '320px',
            }}
          >
            {[...heatmapData].reverse().flatMap((row, reversedY) =>
              row.map((count, x) => {
                const y = cellCount - 1 - reversedY;
                return (
                  <div
                    key={`${y}-${x}`}
                    className={`flex items-center justify-center text-sm font-semibold transition-all duration-200 rounded ${
                      getCellColor(count, x, y, isDark)
                    } hover:opacity-90 cursor-pointer`}
                    title={`Likelihood: ${LIKELIHOOD[y]}, Consequence: ${CONSEQUENCE[x]} - ${count} risk${count !== 1 ? 's' : ''}`}
                  >
                    {count > 0 ? count : ""}
                  </div>
                );
              })
            )}
          </div>
          
          {/* X-axis (consequence) labels */}
          <div className="grid gap-1 mt-2" style={{ gridTemplateColumns: `repeat(${cellCount}, 1fr)` }}>
            {CONSEQUENCE.map((c) => (
              <div key={c} className="text-center text-xs text-muted-foreground">
                {c}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {isLoading && (
        <div className="mt-6 text-center text-sm text-muted-foreground">
          Loading risk data...
        </div>
      )}
      
      {error && (
        <div className="mt-6 text-center text-sm text-red-500">
          {error}
        </div>
      )}
      
      {!isLoading && !error && data && (
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            {data.totalRisks} risk{data.totalRisks !== 1 ? 's' : ''} requiring treatment • Higher risk bottom-right
          </p>
        </div>
      )}
    </div>
  );
});
